/**
 * Widgets for the structure-drawing and reaction-picking inputs that
 * rxn_enum and Molexar need, kept separate from `simpleWidgets.jsx` because
 * each of these owns a modal and some interaction state of its own, rather
 * than being a thin wrapper over one MUI control.
 *
 * All three follow the same contract as every other widget (see
 * `FieldList.jsx`): they render one descriptor against a flat
 * `formState`/`setFormState` pair, and never reach outside it.
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { MoleculeSketcher } from 'BMapsSrc/MoleculeSketcher';
import { ReactionSketcher } from 'BMapsSrc/ReactionSketcher';
import LinkLikeButton from 'BMapsUI/common/LinkLikeButton';
import ReactionSchemePreview from 'BMapsUI/dialog/ImportPane/ImportCompoundPane/Tabs/ReactionSchemePreview';

/**
 * rxn_enum's named-reaction picker: a multi-select over the generated
 * `RXN_ENUM_REACTIONS` vocabulary, with the scheme preview underneath.
 * Blank means "enumerate over every reaction", which is why this field is
 * never `required` — see `RxnEnumJob.jobForm`.
 *
 * Deliberately its own widget rather than a `multiple` flag on
 * `SelectWidget`: that widget is a single-value MUI `Select` whose whole
 * contract (scalar `value`, `MenuItem` children) is single-select, and
 * every other job already depends on it. Picking from a 245-entry
 * vocabulary also wants type-to-search, which is `Autocomplete`, not
 * `Select`.
 */
export function ReactionSelectWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, options = [], placeholder, helperText,
    } = descriptor;
    const value = formState[key] || [];
    return (
        <Box sx={{ width: '50%' }}>
            <Autocomplete
                multiple
                options={options}
                value={value}
                onChange={(_event, newValue) => setFormState({ [key]: newValue })}
                autoHighlight
                disableCloseOnSelect
                filterSelectedOptions
                fullWidth
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={value.length ? '' : placeholder}
                        helperText={helperText}
                    />
                )}
            />
            <ReactionSchemePreview reactions={value} />
        </Box>
    );
}

/**
 * rxn_enum's custom drawn reactions. Each accepted drawing becomes a
 * `{ id, name, rxn }` entry — the shape `rxn_enum_activity.py` already
 * reads (it takes either a raw rxnblock or `{ name, rxn }`, and ignores
 * the `id`, which exists only to key the list here).
 */
export function ReactionListWidget({ descriptor, formState, setFormState }) {
    const { key, addLabel = '+ Draw a custom reaction' } = descriptor;
    const entries = formState[key] || [];
    const [showSketcher, setShowSketcher] = useState(false);

    function addReaction(rxn, drawnName) {
        setFormState({
            [key]: [
                ...entries,
                {
                    id: `${Date.now()}-${entries.length}`,
                    name: drawnName || `Drawn reaction ${entries.length + 1}`,
                    rxn,
                },
            ],
        });
    }

    return (
        <Box sx={{ marginTop: '0.5rem' }}>
            <LinkLikeButton onClick={() => setShowSketcher(true)}>{addLabel}</LinkLikeButton>
            {entries.map((entry) => (
                <Box key={entry.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <span>{entry.name}</span>
                    <LinkLikeButton
                        onClick={() => setFormState({
                            [key]: entries.filter((x) => x.id !== entry.id),
                        })}
                    >
                        remove
                    </LinkLikeButton>
                </Box>
            ))}
            {showSketcher && (
                <ReactionSketcher
                    onAccept={addReaction}
                    onClose={() => setShowSketcher(false)}
                />
            )}
        </Box>
    );
}

/**
 * Molexar's starting-structure input: either typed SMILES or a structure
 * drawn in the sketcher. Both live under ONE descriptor (value shape
 * `{ smiles, molfile }`) rather than two, because they're two ways of
 * saying the same thing and only one can win — keeping them together is
 * what lets "a drawn structure takes precedence" be stated once, in
 * `MolexarJob.buildRequestPayload`, instead of being re-derived from two
 * independent fields. A drawn structure is sent as a molfile, not as
 * browser-generated SMILES, because `molexar_run.py` converts it with full
 * RDKit (which preserves the `[*]` attachment points and multi-fragment
 * `.` drawings the non-de_novo tasks depend on).
 *
 * `example`/`hint` come from the task the user picked — see
 * `MOLEXAR_TASKS` in `MolexarJob.js`.
 */
export function MoleculeSketcherWidget({ descriptor, formState, setFormState }) {
    const {
        key, label, placeholder, hintFrom, exampleFrom,
    } = descriptor;
    const value = formState[key] || {};
    const [showSketcher, setShowSketcher] = useState(false);
    const hint = hintFrom?.(formState);
    const example = exampleFrom?.(formState);

    function setValue(patch) {
        setFormState({ [key]: { ...value, ...patch } });
    }

    return (
        <Box>
            <TextField
                fullWidth
                label={label}
                placeholder={placeholder}
                value={value.smiles || ''}
                onChange={(evt) => setValue({ smiles: evt.target.value })}
                disabled={!!value.molfile}
                helperText={hint}
            />
            <Box sx={{
                display: 'flex', gap: 1, alignItems: 'center', marginTop: '0.25rem',
            }}
            >
                <LinkLikeButton onClick={() => setShowSketcher(true)}>
                    Draw structure
                </LinkLikeButton>
                {value.molfile && (
                    <>
                        <span style={{ fontStyle: 'italic' }}>
                            structure drawn — SMILES field ignored
                        </span>
                        <LinkLikeButton onClick={() => setValue({ molfile: '' })}>
                            clear
                        </LinkLikeButton>
                    </>
                )}
            </Box>
            {example && !value.molfile && (
                <Box sx={{ marginTop: '0.25rem' }}>
                    <code>{example}</code>
                    {' '}
                    <LinkLikeButton onClick={() => setValue({ smiles: example })}>
                        Use this example
                    </LinkLikeButton>
                </Box>
            )}
            {showSketcher && (
                <MoleculeSketcher
                    onAccept={(molfile) => setValue({ molfile })}
                    onClose={() => setShowSketcher(false)}
                />
            )}
        </Box>
    );
}
